import * as React from 'react';
import { Folder, FolderDashed, FolderOpen, File, FileDashed, DotsThree, Lightning, Plus } from '@phosphor-icons/react';
import { Key, TreeItemContentRenderProps, TreeStateContext, Button as AriaButton } from 'react-aria-components';

import { ArticleNavItem, ItemLinkComponent } from '../types';
import { useSidebar } from '../context';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Spinner } from '@/components/ui/spinner';

type ArticleItemTreeNode = {
  key: Key;
  value: ArticleNavItem;
  children: ArticleItemTreeNode[] | null;
};

function FolderSlotButton({ children }: { children: React.ReactNode }) {
  return (
    <AriaButton slot="chevron" className="relative z-30 flex p-0">
      <span className="relative inline-flex size-3 items-center justify-center">{children}</span>
    </AriaButton>
  );
}

export interface ArticleItemProps extends TreeItemContentRenderProps {
  ItemLinkComponent: ItemLinkComponent;
  item: ArticleItemTreeNode;
  onAddArticle?: (parentId: string) => void;
  isAddingArticle?: boolean;
  isLoadingChildren?: boolean;
  onMoreActions?: (articleId: string) => void;
}

export function ArticleItem({
  item,
  hasChildItems,
  isSelected,
  isExpanded,
  ItemLinkComponent,
  onAddArticle,
  isAddingArticle,
  isLoadingChildren,
  onMoreActions,
  level,
}: ArticleItemProps) {
  const { isSearching } = useSidebar();
  const treeState = React.useContext(TreeStateContext);
  const shouldPreventNavigation = Boolean(isSelected);

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddArticle?.(item.value.id);
  };

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoreActions?.(item.value.id);
  };

  const handleItemClick = (e: React.MouseEvent) => {
    if (shouldPreventNavigation) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (hasChildItems && treeState) {
      treeState.toggleKey(item.key);
    }
  };

  const showActions = !!((onAddArticle && !isAddingArticle) || onMoreActions);

  const isDraft = item.value.status === 'draft';

  let statusLabel: string;
  switch (item.value.status) {
    case 'draft':
      statusLabel = '(draft)';
      break;
    case 'unpublished':
      statusLabel = '(unpublished changes)';
      break;
    default:
      statusLabel = '';
  }

  const folderIcon = (
    <FolderSlotButton>
      <Folder
        aria-hidden="true"
        weight="fill"
        className={cn(
          'absolute size-3 transition-opacity duration-200 ease-out',
          isExpanded ? 'opacity-0' : 'opacity-100',
        )}
      />
      <FolderOpen
        aria-hidden="true"
        weight="fill"
        className={cn(
          'absolute size-3 transition-opacity duration-200 ease-out',
          isExpanded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </FolderSlotButton>
  );

  const folderWithChangesIcon = (
    <FolderSlotButton>
      <FolderDashed
        aria-hidden="true"
        weight="fill"
        className={cn(
          'absolute size-3 transition-opacity duration-200 ease-out',
          isExpanded ? 'opacity-0' : 'opacity-100',
        )}
      />
      <FolderOpen
        aria-hidden="true"
        weight="fill"
        className={cn(
          'absolute size-3 transition-opacity duration-200 ease-out',
          isExpanded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </FolderSlotButton>
  );

  const discoveryIconContent = (
    <>
      <span className="sr-only">Knowledge Discovery</span>
      <Lightning aria-hidden="true" weight="fill" className="size-3 shrink-0 text-blue-600" />
    </>
  );

  const discoveryIcon = hasChildItems ? (
    <FolderSlotButton>
      <span className="text-blue-600">{discoveryIconContent}</span>
    </FolderSlotButton>
  ) : (
    <span className="flex size-3 shrink-0 items-center justify-center text-blue-600">{discoveryIconContent}</span>
  );

  const fileIcon = (
    <span className="flex size-3 shrink-0 items-center justify-center">
      <File aria-hidden="true" className="size-3" />
    </span>
  );

  const fileWithChangesIcon = (
    <span className="flex size-3 shrink-0 items-center justify-center">
      <span className="sr-only">Unpublished Changes</span>
      <FileDashed aria-hidden="true" className="size-3" />
    </span>
  );

  const loadingFolderIcon = (
    <span
      className="flex size-3 shrink-0 items-center justify-center"
      role="status"
      aria-label={`Loading nested articles for ${item.value.label}`}
    >
      <Spinner aria-hidden="true" className="size-3 text-current" />
      <span className="sr-only">Loading nested articles for {item.value.label}</span>
    </span>
  );

  const renderStatusIcon = (status: ArticleNavItem['status'], hasChildItems: boolean) => {
    if (status === 'discovery') {
      return discoveryIcon;
    }

    const hasChanges = status === 'unpublished' || status === 'draft';

    if (hasChildItems) {
      return hasChanges ? folderWithChangesIcon : folderIcon;
    } else {
      return hasChanges ? fileWithChangesIcon : fileIcon;
    }
  };

  return (
    <div
      style={{ ['--line-offset' as string]: '0.875rem' }}
      aria-busy={isLoadingChildren || undefined}
      className={cn(
        'group relative flex items-center gap-1 rounded-md py-1 pl-1 ml-[calc((var(--tree-item-level)-1)*var(--line-offset))] pr-4 h-9',
        isDraft && 'text-subtlest italic',
        isSelected
          ? 'bg-lime-100 text-default font-bold after:absolute after:-right-0.5 after:top-1/2 after:h-1/2 after:w-1 after:-translate-y-1/2 after:bg-lime-400'
          : 'hover:bg-dark-200 hover:text-accent-foreground',
      )}
    >
      {Array.from({ length: level - 1 }, (_, i) => (
        <span
          key={`${item.value.label}-${i + 1}`}
          style={{ ['--item-line-level' as string]: i + 1 }}
          // Don't mess with the calc: forumula for multiple lines.
          className="absolute w-[1px] bg-dark-400 -top-2 h-9 left-[calc(-4px-var(--line-offset)*(var(--item-line-level)-1))]"
          aria-hidden={true}
        />
      ))}

      <AriaButton slot="drag" className="sr-only">
        Drag {item.value.label}
      </AriaButton>

      {isSearching ? null : isLoadingChildren ? loadingFolderIcon : renderStatusIcon(item.value.status, hasChildItems)}

      <ItemLinkComponent
        href={item.value.href}
        title={`${item.value.label} ${statusLabel}`}
        data-sidebar-item-id={item.value.id}
        data-sidebar-item-type="article"
        className={cn(
          'min-w-0 text-inherit text-sm truncate flex-1 after:absolute after:inset-0 after:z-10 pr-1',
          isSelected && 'text-default',
        )}
        onClick={handleItemClick}
      >
        {item.value.label}
      </ItemLinkComponent>

      {showActions && (
        <ButtonGroup
          orientation="horizontal"
          className={cn(
            'absolute h-4 my-auto right-0.5 top-0 bottom-0 z-20 hidden items-center rounded-xl',
            'group-hover:flex group-focus-within:flex',
            isSelected ? 'bg-lime-100 text-default' : 'bg-dark-200',
          )}
        >
          {onMoreActions && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`More actions for ${item.value.label}`}
              className="size-4 [&_svg]:size-3.5"
              onClick={handleMoreClick}
            >
              <DotsThree aria-hidden="true" weight="bold" />
            </Button>
          )}

          {onAddArticle && !isAddingArticle && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Add article to ${item.value.label}`}
              className="size-4 [&_svg]:size-3.5"
              onClick={handleAddClick}
            >
              <Plus aria-hidden="true" />
            </Button>
          )}
        </ButtonGroup>
      )}
    </div>
  );
}
